import { createApi } from '@reduxjs/toolkit/query/react';
import {
  ProductDetails,
  IdentifierValidationResponse,
  ProductRegistrationList,
  ProductQuery,
  ProductOfferings
} from 'models/product';
import {
  FinalizeProductRegistrationData,
  ProductRegistrationInfo,
  ProductRegistrationQuery
} from 'models/productRegistration';
import {
  defaultHeaders,
  baseQueryAuth,
  createFilterUrl,
  createQueryUrl
} from 'utils/fetchUtils/fetchUtils';
import { trim } from 'utils/stringUtils';
import { createProductRegistrationBody } from 'utils/serviceUtils';
import * as config from 'config';

export const productApi = createApi({
  reducerPath: 'productApi',
  baseQuery: baseQueryAuth,
  tagTypes: ['Product'],
  endpoints: (builder) => ({
    getRegisteredProductList: builder.query<
      ProductRegistrationList,
      ProductQuery
    >({
      query: ({ pagination, languageCode = 'en' }) => {
        let url = createFilterUrl('/product-registrations', {
          languageCode: trim(languageCode)
        });

        if (pagination?.limit) {
          url += `&limit=${pagination.limit}`;
        }

        if (pagination?.cursor) {
          url += `&cursor=${pagination.cursor}`;
        }

        return url;
      },
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, newItems, { arg }) => {
        if (arg.pagination?.cursor) {
          currentCache.productRegistrations.push(
            ...newItems.productRegistrations
          );
          return currentCache;
        }
        return newItems;
      },
      // Refetch when the page arg changes
      forceRefetch({ currentArg, previousArg }) {
        return (
          currentArg?.pagination?.cursor !== previousArg?.pagination?.cursor
        );
      },
      transformResponse: (res: any) => res.data.attributes,
      providesTags: [{ type: 'Product', id: 'ProductList' }]
    }),
    getRegisteredProductDetails: builder.query<ProductDetails, ProductQuery>({
      query: ({ iprId, languageCode = 'en' }) =>
        createFilterUrl(`/product-registrations/${trim(iprId)}`, {
          languageCode: trim(languageCode)
        }),
      transformResponse: (res: any) => res.data.attributes,
      providesTags: (_res, _err, { iprId }) => [
        { type: 'Product', id: iprId ?? undefined }
      ]
    }),
    registerProducts: builder.mutation<any, ProductRegistrationQuery>({
      async queryFn(
        { customer, product, soldDate },
        _queryApi,
        _extraOptions,
        fetch
      ) {
        // Registration
        const { data, error } = await fetch({
          url: `${config.backendApi}/customers/${customer.id}/register-products`,
          headers: defaultHeaders,
          method: 'POST',
          body: {
            data: createProductRegistrationBody({
              product,
              customer,
              soldDate
            })
          }
        });

        if (error) {
          return { error };
        }

        return { data };
      },
      invalidatesTags: ['Product']
    }),
    registerProfessionalProducts: builder.mutation<
      FinalizeProductRegistrationData,
      string
    >({
      query: (code: string) => ({
        url: `/product-registrations/professional/${code}`,
        method: 'POST'
      }),
      transformResponse: (response: any) => response.data
    }),
    getProductOfferings: builder.query<ProductOfferings, ProductQuery>({
      query: ({ iprId }) => ({
        url: `/product-registrations/offerings/${iprId}`,
        method: 'GET'
      }),
      transformResponse: (response: any) => response.data
    }),
    getProductRegistrationInfo: builder.query<
      ProductRegistrationInfo,
      ProductQuery
    >({
      query: ({
        pnc,
        serialNumber,
        husqvarnaId,
        iprId,
        languageCode = 'en'
      }) => ({
        url: createFilterUrl(
          `/product-registrations/product-info/${languageCode}`,
          {
            husqvarnaId: trim(husqvarnaId),
            serialNumber: trim(serialNumber),
            pnc: trim(pnc),
            iprId: trim(iprId)
          }
        ),
        method: 'GET'
      }),
      transformResponse: (response: any) => response.data.attributes
    }),
    validateProductIdentifiers: builder.query<
      IdentifierValidationResponse,
      ProductQuery
    >({
      query: ({ husqvarnaId, pnc, serialNumber }) => ({
        url: createQueryUrl('/product-registrations/validate-identifiers', {
          hid: trim(husqvarnaId),
          pnc: trim(pnc),
          serialNumber: trim(serialNumber)
        }),
        method: 'GET'
      })
    }),
    deleteRegisteredProduct: builder.mutation<void, ProductQuery>({
      query: ({ iprId, languageCode = 'en' }) => ({
        url: createFilterUrl(`/product-registrations/${trim(iprId)}`, {
          languageCode: trim(languageCode)
        }),
        method: 'DELETE'
      }),
      invalidatesTags: (_res, _err, { iprId }) => [
        { type: 'Product', id: iprId ?? undefined },
        { type: 'Product', id: 'ProductList' }
      ]
    })
  })
});

export const {
  useGetRegisteredProductListQuery,
  useGetRegisteredProductDetailsQuery,
  useRegisterProfessionalProductsMutation,
  useRegisterProductsMutation,
  useGetProductOfferingsQuery,
  useGetProductRegistrationInfoQuery,
  useValidateProductIdentifiersQuery,
  useLazyValidateProductIdentifiersQuery,
  useDeleteRegisteredProductMutation
} = productApi;
